import { useState, useEffect, useMemo, useRef } from "react";
import {
  CheckCircle2, X, ArrowRight, RotateCcw, MessageCircle,
  BookmarkPlus, ListChecks, Library,
} from "lucide-react";
import { API_HOST } from "../../constants";
import type { RoadmapNode } from "../../data/ch_concepts";
import type { Task, TelegramUser } from "../../types";
import { AITutorMessageContent } from "../AITutorMessageContent";
import { AITutorSources, type SourceItem } from "../AITutorSources";
import { TopicGenerationPipeline } from "./TopicGenerationPipeline";

// ─── Node content types ────────────────────────────────────────────────────────

const MIN_TOPIC_THEORY_LEN = 400;

interface QuizQuestion {
  question: string;
  options: string[];
  correct: number;
}

/** Курируемые ссылки с саммари, порядок = order (1 — сначала). */
export interface StudyMaterialItem {
  url: string;
  title: string;
  summary: string;
  order?: number;
}

interface NodeContent {
  label: string;
  theory: string;
  example_sql: string;
  problems: string[];
  practices: string[];
  key_points: string[];  // legacy fallback
  practice_tags: string[];
  ask_tutor_questions?: string[];
  quiz: QuizQuestion[];
  docs_url?: string;
  study_materials?: StudyMaterialItem[];
  sources?: SourceItem[];
}

/** Согласовано с бэкендом (_is_valid_node_content): не кэшируем и не показываем «битый» урок. */
function isTopicLessonContentValid(c: NodeContent | null | undefined): boolean {
  const t = c?.theory?.trim() ?? "";
  if (t.length < MIN_TOPIC_THEORY_LEN) return false;
  if (!t.includes("## Разбор")) return false;
  if (!t.includes("## Частые ошибки")) return false;
  if (!t.includes("## Лучшие практики")) return false;
  return true;
}

// Sections that arrive progressively via SSE before the final done event
interface StreamedSections {
  problems?: string[];
  practices?: string[];
  sql?: string;
  docs_url?: string;
  ask_tutor_questions?: string[];
  quiz?: QuizQuestion[];
  sources?: SourceItem[];
  study_materials?: StudyMaterialItem[];
}

// ─── Topic payload (includes area context) ─────────────────────────────────────

export interface TopicSelectPayload {
  node: RoadmapNode;
  areaLabel: string;
  areaDescription: string;
}

// ─── Quiz component — styled like QuizCard ─────────────────────────────────────

interface TopicQuizProps {
  questions: QuizQuestion[];
  onMarkLearned?: () => void;
  onPostpone?: () => void;
  actionDone?: boolean;
}

function TopicQuiz({ questions, onMarkLearned, onPostpone, actionDone }: TopicQuizProps) {
  const [answers, setAnswers] = useState<Record<number, number>>({});

  if (!questions.length) return null;
  const q = questions[0];
  const chosen = answers[0];
  const revealed = chosen !== undefined;

  return (
    <div className="rounded-xl border-2 border-dashed border-indigo-200/90 bg-gradient-to-b from-indigo-50/80 to-white p-3 shadow-sm">
      <p className="flex items-center gap-2 text-sm font-semibold text-indigo-950 mb-1">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 shrink-0">
          <ListChecks className="w-4 h-4" strokeWidth={2.25} />
        </span>
        Проверь себя
      </p>
      <p className="text-[11px] font-medium uppercase tracking-wide text-indigo-600/80 mb-2">Опрос с одним верным ответом</p>
      <p className="text-sm text-neutral-text mb-2.5">{q.question}</p>
      <div className="space-y-1.5">
        {q.options.map((opt, oi) => {
          const isChosen = chosen === oi;
          const isCorrect = q.correct === oi;
          let cls =
            "border-indigo-200/80 bg-white hover:border-indigo-400/60 hover:bg-indigo-50/50 cursor-pointer ring-1 ring-transparent";
          if (revealed) {
            if (isCorrect) cls = "border-green-500/50 bg-green-50 text-green-800 cursor-default ring-green-200/50";
            else if (isChosen) cls = "border-red-300 bg-red-50 text-red-700 cursor-default";
            else cls = "border-neutral-border/60 bg-neutral-light/50 text-neutral-text/60 cursor-default";
          }
          return (
            <button
              key={oi}
              type="button"
              disabled={revealed}
              onClick={() => setAnswers({ 0: oi })}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors text-sm ${cls}`}
            >
              <span className="font-semibold mr-1.5 tabular-nums text-indigo-500/90">{String.fromCharCode(65 + oi)}.</span>
              {opt}
            </button>
          );
        })}
      </div>
      {revealed && (
        <p className={`mt-2 text-xs font-medium ${chosen === q.correct ? "text-green-700" : "text-red-600"}`}>
          {chosen === q.correct ? "✓ Верно!" : `✗ Правильный ответ: ${String.fromCharCode(65 + q.correct)}`}
        </p>
      )}
      {revealed && !actionDone && (onMarkLearned || onPostpone) && (
        <div className="mt-3 pt-3 border-t border-indigo-100 flex flex-col gap-2">
          <p className="text-xs text-neutral-text/50">Что сделать с этой темой?</p>
          <div className="flex gap-2">
            {onMarkLearned && (
              <button
                type="button"
                onClick={onMarkLearned}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-medium transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Отметить как изученную
              </button>
            )}
            {onPostpone && (
              <button
                type="button"
                onClick={onPostpone}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-border bg-white hover:bg-neutral-light text-neutral-text/70 text-xs font-medium transition-colors"
              >
                <BookmarkPlus className="w-3.5 h-3.5" />
                На потом
              </button>
            )}
          </div>
        </div>
      )}
      {revealed && actionDone && (
        <p className="mt-3 text-xs text-green-700 font-medium">✓ Статус сохранён</p>
      )}
    </div>
  );
}

// ─── Topic content panel ───────────────────────────────────────────────────────

interface TopicPanelProps {
  node: RoadmapNode;
  areaLabel: string;
  areaDescription: string;
  tasks?: Task[];
  user: TelegramUser | null;
  onClose: () => void;
  onAskTutor?: (question: string) => void;
  onMarkLearned?: (nodeId: string) => void;
  onPostpone?: (nodeId: string) => void;
  learnedNodeIds?: Set<string>;
}

export function TopicPanel({ node, areaLabel, areaDescription, tasks = [], user, onClose, onAskTutor, onMarkLearned, onPostpone, learnedNodeIds }: TopicPanelProps) {
  const [content, setContent] = useState<NodeContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [theoryTokens, setTheoryTokens] = useState("");
  const [previewTokens, setPreviewTokens] = useState("");
  const previewBufRef = useRef("");
  const previewFlushRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sections, setSections] = useState<StreamedSections>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const regenerateRef = useRef(false);
  const [quizActionDone, setQuizActionDone] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [revealStep, setRevealStep] = useState(0);

  useEffect(() => {
    const cacheKey = `topic_content::${node.id}::${areaLabel}`;

    setContent(null);
    setTheoryTokens("");
    setPreviewTokens("");
    previewBufRef.current = "";
    if (previewFlushRef.current) clearInterval(previewFlushRef.current);
    previewFlushRef.current = setInterval(() => {
      if (previewBufRef.current) {
        setPreviewTokens((prev) => prev + previewBufRef.current);
        previewBufRef.current = "";
      }
    }, 80);
    setSections({});
    setLoading(true);
    setQuizActionDone(false);
    setStreamError(null);
    setRevealStep(0);

    let skipCache = false;
    if (!regenerateRef.current) {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached) as NodeContent;
          if (isTopicLessonContentValid(parsed)) {
            setContent(parsed);
            setLoading(false);
            return;
          }
          sessionStorage.removeItem(cacheKey);
        }
      } catch { /* ignore */ }
    } else {
      try {
        sessionStorage.removeItem(cacheKey);
      } catch { /* ignore */ }
      skipCache = true;
      regenerateRef.current = false;
    }

    const ctrl = new AbortController();
    const params = new URLSearchParams({ area_label: areaLabel, area_description: areaDescription });
    if (skipCache) params.set("regenerate", "1");

    (async () => {
      try {
        const res = await fetch(
          `${API_HOST}/api/ai/assessment/node-content/${node.id}/stream?${params}`,
          { signal: ctrl.signal }
        );
        if (!res.ok || !res.body) throw new Error("fetch failed");

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;
            let evt: Record<string, unknown>;
            try { evt = JSON.parse(raw); } catch { continue; }

            if (evt.type === "preview") {
              previewBufRef.current += evt.text as string;
            } else if (evt.type === "preview_reset") {
              previewBufRef.current = "";
              setPreviewTokens("");
            } else if (evt.type === "token") {
              setTheoryTokens((prev) => prev + (evt.text as string));
            } else if (evt.type === "sources") {
              setSections((prev) => ({ ...prev, sources: evt.sources as SourceItem[] }));
            } else if (evt.type === "section") {
              const key = evt.key as string;
              const data = evt.data;
              setSections((prev) => ({
                ...prev,
                ...(key === "sql" ? { sql: data as string } : { [key]: data }),
              }));
            } else if (evt.type === "error") {
              const msg = typeof evt.message === "string" ? evt.message : "Не удалось загрузить урок.";
              setStreamError(msg);
              setTheoryTokens("");
              setPreviewTokens("");
              previewBufRef.current = "";
              if (previewFlushRef.current) { clearInterval(previewFlushRef.current); previewFlushRef.current = null; }
              setSections({});
              setLoading(false);
            } else if (evt.type === "done") {
              const nodeContent = evt.content as NodeContent;
              setStreamError(null);
              setContent(nodeContent);
              setLoading(false);
              setPreviewTokens("");
              previewBufRef.current = "";
              if (previewFlushRef.current) { clearInterval(previewFlushRef.current); previewFlushRef.current = null; }
              setSections({});  // content now authoritative
              if (isTopicLessonContentValid(nodeContent)) {
                try { sessionStorage.setItem(cacheKey, JSON.stringify(nodeContent)); } catch { /* ignore */ }
              }
            }
          }
        }
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setStreamError("Сеть или сервер недоступны. Попробуй позже.");
        setTheoryTokens("");
        setSections({});
        setContent(null);
        setLoading(false);
      }
    })();

    return () => {
      ctrl.abort();
      if (previewFlushRef.current) { clearInterval(previewFlushRef.current); previewFlushRef.current = null; }
    };
  }, [node.id, areaLabel, areaDescription, refreshKey]);

  const handleRegenerate = () => {
    regenerateRef.current = true;
    setRefreshKey((k) => k + 1);
  };

  const statusBadge =
    node.status === "known"
      ? { label: "Знаешь ✓", cls: "bg-green-100 text-green-700 border-green-200" }
      : node.status === "next"
      ? { label: "Следующий шаг", cls: "bg-blue-100 text-blue-700 border-blue-200" }
      : { label: "Нужно изучить", cls: "bg-gray-100 text-gray-600 border-gray-200" };

  // Merge: sections stream in before done, content is authoritative after done
  const displayDocsUrl = content?.docs_url ?? sections.docs_url;
  const displayAskTutorQuestions = content?.ask_tutor_questions ?? sections.ask_tutor_questions;
  const displayQuiz = content?.quiz ?? sections.quiz;

  const displayStudyMaterials = useMemo(() => {
    const raw = content?.study_materials ?? sections.study_materials;
    if (!raw?.length) return [];
    return [...raw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [content?.study_materials, sections.study_materials]);

  const isAlreadyLearned = learnedNodeIds?.has(node.id) ?? false;

  const handleMarkLearned = () => {
    setQuizActionDone(true);
    onMarkLearned?.(node.id);
  };

  const handlePostpone = () => {
    setQuizActionDone(true);
    onPostpone?.(node.id);
  };

  const practiceTasks =
    (content?.practice_tags ?? []).length
      ? tasks.filter((t) => t.tag?.some((tag) => content!.practice_tags.includes(tag))).slice(0, 2)
      : [];

  const markdownContent = useMemo(() => {
    if (loading) {
      const t = theoryTokens.trim();
      return t;
    }
    const parts: string[] = [];
    const theory = (content?.theory ?? "").trim();
    if (theory) parts.push(theory);
    const sql = (content?.example_sql ?? "").trim();
    if (sql) {
      parts.push("## Пример SQL\n\n[demo:\n" + sql + "\n]");
    }
    return parts.join("\n\n");
  }, [loading, theoryTokens, content?.theory, content?.example_sql]);

  useEffect(() => {
    if (loading || !content || streamError) {
      setRevealStep(0);
      return;
    }
    setRevealStep(0);
    const max = 6;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let s = 1; s <= max; s++) {
      timers.push(window.setTimeout(() => setRevealStep(s), s * 110));
    }
    return () => timers.forEach(clearTimeout);
  }, [loading, content, streamError]);

  const sourcesForDocs = useMemo(() => {
    const list = content?.sources ?? sections.sources;
    if (list?.length) return list;
    if (displayDocsUrl)
      return [{ content: displayDocsUrl, metadata: { url: displayDocsUrl, title: "Официальная документация" }, score: 1 }];
    return [];
  }, [content?.sources, sections.sources, displayDocsUrl]);

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-white overflow-hidden">
      <div className="flex-shrink-0 flex items-start justify-between gap-2 px-4 py-3 border-b border-neutral-border bg-neutral-light/60">
        <div className="min-w-0">
          <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border font-medium mb-1 ${statusBadge.cls}`}>
            {statusBadge.label}
          </span>
          <h3 id="roadmap-topic-dialog-title" className="text-sm font-semibold text-neutral-text leading-snug">
            {node.label}
          </h3>
          {areaLabel && (
            <p className="text-[11px] text-neutral-text/40 mt-0.5">{areaLabel}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-neutral-border bg-white hover:bg-neutral-light text-neutral-text text-xs font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none"
            title="Перегенерировать материал"
            aria-label="Перегенерировать материал"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Перегенерировать
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-border/40 text-neutral-text/40 hover:text-neutral-text transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-5 min-h-0">
        {streamError && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800 space-y-2"
            role="alert"
          >
            <p className="leading-snug">{streamError}</p>
            <button
              type="button"
              onClick={() => {
                setStreamError(null);
                setRefreshKey((k) => k + 1);
              }}
              className="text-xs font-semibold text-red-900 underline-offset-2 hover:underline"
            >
              Повторить загрузку
            </button>
          </div>
        )}

        {loading && !streamError && (
          <div className="space-y-3 pb-1">
            <TopicGenerationPipeline theoryTokens={theoryTokens} previewTokens={previewTokens} sections={sections} />
            {!theoryTokens && (
              <p className="text-center text-xs text-neutral-text/45 px-2">
                Модель собирает полный ответ — обычно до минуты.
              </p>
            )}
          </div>
        )}

        {!loading && !streamError && revealStep >= 1 && displayStudyMaterials.length > 0 && (
          <section
            className="rounded-xl border-2 border-amber-200/90 bg-gradient-to-b from-amber-50/95 to-white p-4 shadow-sm ring-1 ring-amber-100/70"
            aria-labelledby="topic-study-materials-heading"
          >
            <div className="flex items-start gap-3 mb-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-800 shrink-0">
                <Library className="w-5 h-5" strokeWidth={2.25} aria-hidden />
              </span>
              <div className="min-w-0">
                <h4 id="topic-study-materials-heading" className="text-sm font-semibold text-amber-950 leading-snug">
                  Полезные материалы
                </h4>
                <p className="text-[11px] text-amber-900/75 mt-0.5 leading-relaxed">
                  Главный трек урока: сначала пройди ссылки по порядку — так проще связать теорию с практикой и примером SQL ниже.
                </p>
              </div>
            </div>
            <ol className="space-y-2.5 list-none m-0 p-0">
              {displayStudyMaterials.map((m, i) => (
                <li
                  key={`${m.url}-${i}`}
                  className="flex gap-3 rounded-xl border border-amber-100/90 bg-white/95 p-3 shadow-sm"
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-900 text-xs font-bold tabular-nums"
                    aria-hidden
                  >
                    {m.order ?? i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-primary hover:underline break-words"
                    >
                      {m.title}
                    </a>
                    <p className="text-xs text-neutral-text/80 mt-1 leading-relaxed">{m.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {((loading && theoryTokens.length > 0) || (!loading && !streamError && markdownContent.length > 0)) && (
          <div
            className={
              loading
                ? "prose prose-sm max-w-none min-w-0"
                : `prose prose-sm max-w-none min-w-0 transition-all duration-300 ease-out ${
                    revealStep >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
                  }`
            }
          >
            <AITutorMessageContent
              content={markdownContent}
              user={user}
              extractFollowUps={false}
              showFollowUpButtons={false}
              codeBlocksPlaceholder={loading}
              loadingStage={loading ? "generating_sql" : null}
            />
          </div>
        )}

        {!loading && !streamError && revealStep >= 3 && sourcesForDocs.length > 0 && (
          <AITutorSources
            sources={sourcesForDocs}
            showEmptyMessage={false}
            variant="default"
            popupTitle="Источники"
          />
        )}

        {!loading && !streamError && revealStep >= 4 && practiceTasks.length > 0 && (
          <section>
            <h4 className="text-[10px] font-bold uppercase tracking-wide text-primary mb-2">Практика по теме</h4>
            <div className="space-y-1.5">
              {practiceTasks.map((task) => (
                <a
                  key={task.id}
                  href={`/task/${task.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-text truncate">{task.title}</p>
                    {task.tag && task.tag.length > 0 && (
                      <p className="text-[10px] text-neutral-text/50 truncate">{task.tag.slice(0, 3).join(" · ")}</p>
                    )}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-primary/60 group-hover:text-primary flex-shrink-0 transition-colors" />
                </a>
              ))}
            </div>
          </section>
        )}

        {!loading && !streamError && revealStep >= 5 && onAskTutor && (displayAskTutorQuestions?.length ?? 0) > 0 && (
          <section className="rounded-xl border border-primary/25 bg-primary/[0.06] p-3 border-l-[3px] border-l-primary">
            <p className="flex items-center gap-2 text-sm font-medium text-neutral-text mb-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary shrink-0">
                <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.25} />
              </span>
              Спросить AI Tutor
            </p>
            <p className="text-xs text-neutral-text/55 mb-2 pl-0.5">Готовые формулировки — откроется чат с ответом</p>
            <div className="flex flex-col gap-1.5">
              {displayAskTutorQuestions!.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onAskTutor(q)}
                  className="text-left px-3 py-2.5 rounded-2xl border border-primary/15 bg-white/90 hover:bg-white hover:border-primary/35 hover:shadow-sm text-sm text-neutral-text transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          </section>
        )}

        {!loading && !streamError && revealStep >= 6 && (displayQuiz?.length ?? 0) > 0 && (
          <TopicQuiz
            questions={displayQuiz!}
            onMarkLearned={!isAlreadyLearned && onMarkLearned ? handleMarkLearned : undefined}
            onPostpone={onPostpone ? handlePostpone : undefined}
            actionDone={quizActionDone || isAlreadyLearned}
          />
        )}

        {/* Standalone mark-learned button when quiz is absent */}
        {!loading && !streamError && revealStep >= 6 && (displayQuiz?.length ?? 0) === 0 && !isAlreadyLearned && !quizActionDone && (onMarkLearned || onPostpone) && (
          <div className="flex gap-2 pt-1">
            {onMarkLearned && (
              <button
                type="button"
                onClick={handleMarkLearned}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Отметить как изученную
              </button>
            )}
            {onPostpone && (
              <button
                type="button"
                onClick={handlePostpone}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-neutral-border bg-white hover:bg-neutral-light text-neutral-text/70 text-sm font-medium transition-colors"
              >
                <BookmarkPlus className="w-4 h-4" />
                На потом
              </button>
            )}
          </div>
        )}

        {!loading && !streamError && revealStep >= 6 && (displayQuiz?.length ?? 0) === 0 && (isAlreadyLearned || quizActionDone) && (
          <p className="text-xs text-green-700 font-medium pt-1">✓ Статус сохранён</p>
        )}
      </div>
    </div>
  );
}
